'use client';

import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import type {
  OfficeWithPartialCity,
  SettlementWithClaimSectionAndOffice,
  ClaimSectionWithPages,
} from '@jlg-website/database/types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus } from 'lucide-react';

export const loading = () => <>Loading...</>;

const AttorneyForm = dynamic(
  () => import('@/components/admin/attorneys/attorney-form').then((mod) => mod.AttorneyForm),
  { loading }
);

type AttorneyFormData = {
  offices: OfficeWithPartialCity[];
  settlements: SettlementWithClaimSectionAndOffice[];
  claimSections: Partial<ClaimSectionWithPages>[];
};

export function AttorneyCreateDialogForm({ onCreate }: { onCreate: () => void }) {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<AttorneyFormData | null>(null);

  const handleSave = () => {
    setOpen(false);
    onCreate();
  };

  useEffect(() => {
    if (!open) return;

    const fetchFormData = async () => {
      const request = await fetch('/admin/api/attorneys/form-data');
      const data = await request.json();
      setFormData(data);
    };

    fetchFormData();
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        if (!value) setFormData(null);
      }}
    >
      <DialogTrigger asChild>
        <Button className="w-full">
          <Plus /> <strong>Create Attorney</strong>
        </Button>
      </DialogTrigger>
      <DialogContent className="lg:min-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Attorney</DialogTitle>
          {formData && (
            <AttorneyForm
              offices={formData.offices}
              settlements={formData.settlements}
              claimSections={formData.claimSections}
              onSave={handleSave}
              customCancelButton={
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
              }
            />
          )}
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}

export default AttorneyCreateDialogForm;
